import { formatDate, type Words } from "./copy";
import type { Language, PreviewDoc } from "../types";

const STEPS = [
  { id: "preparing", key: "progressPreparing" },
  { id: "making", key: "progressMaking" },
  { id: "shipping", key: "progressShipping" },
  { id: "arrived", key: "progressArrived" },
] as const;

/** Where a paid order has got to, on the customer's own page. The step comes
 *  from the Mac app through the server, so a day that says nothing new simply
 *  keeps the date of the last change. A step this build does not know shows
 *  nothing rather than a wrong place in the line. */
export default function OrderProgress({
  fulfilment,
  language,
  words,
}: {
  fulfilment: NonNullable<PreviewDoc["fulfilment"]>;
  language: Language;
  words: Words;
}) {
  const now = STEPS.findIndex((s) => s.id === fulfilment.step);
  if (now < 0) return null;
  const arrived = STEPS[now]!.id === "arrived";
  return (
    <section className="progress" aria-labelledby="progress-title">
      <h2 className="progress-title" id="progress-title">
        {words.t("progressTitle")}
      </h2>
      <ol className="progress-steps">
        {STEPS.map((step, i) => {
          // The last step is not under way once it is reached: it is done.
          const done = i < now || (arrived && i === now);
          return (
            <li
              key={step.id}
              className={`progress-step${done ? " progress-done" : i === now ? " progress-now" : ""}`}
              aria-current={i === now ? "step" : undefined}
            >
              <span className="progress-dot" aria-hidden="true">
                {done ? "✓" : ""}
              </span>
              {words.t(step.key)}
            </li>
          );
        })}
      </ol>
      <p className="progress-at">{words.t("progressUpdated", { date: formatDate(fulfilment.at, language) })}</p>
    </section>
  );
}
